import axiosInstance from 'integration/api/axiosInstance';
import {
  CommonEntitiesListResponse,
  PatientBriefCreationDto,
  PatientResponseDto,
  PatientUpdateDto,
  PatientSubscriptionsProAppResponseDto,
  FamilyMemberResponseDto,
  FamilyMemberUpdateDto,
  FamilyMemberCreationDto,
  PatientSearchResponseDto,
} from '@docbay/schemas';
import { FetchPatientsParams } from 'common/types/patients';
import axiosBlobInstance from './axiosBlobInstance';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';

/**
 * Patients API service.
 */
export const PatientsApi = {
  /**
   * Fetch patients list.
   *
   * @param params - Fetch patients parameters.
   * @returns Promise resolving to CommonEntitiesListResponse of PatientResponseDto.
   */
  fetchPatients: (params: FetchPatientsParams) => {
    const { search, page, limit, clinicIds } = params;
    return axiosInstance.get<CommonEntitiesListResponse<PatientResponseDto>>(
      '/patients',
      {
        params: {
          ...(search ? { search } : {}),
          ...(clinicIds?.length ? { clinicIds } : {}),
          page,
          limit,
        },
      },
    );
  },

  /**
   * Fetch patients and family members.
   *
   * @param params - Search string.
   * @returns Promise resolving to array of PatientSearchResponseDto.
   */
  fetchPatientsAndFamilyMembers: (params: { search: string }) =>
    axiosInstance.get<PatientSearchResponseDto[]>('/patients/search', {
      params,
    }),

  /**
   * Create a new patient.
   *
   * @param data - Patient brief creation DTO.
   * @returns Promise resolving to PatientResponseDto.
   */
  createPatients: (data: PatientBriefCreationDto) =>
    axiosInstance.post<PatientResponseDto>('/patients/brief', data),

  /**
   * Update an existing patient.
   *
   * @param id - Patient ID.
   * @param data - Patient update DTO.
   * @returns Promise resolving to PatientResponseDto.
   */
  updatePatients: (id: string, data: PatientUpdateDto) =>
    axiosInstance.patch<PatientResponseDto>(`/patients/${id}`, data),

  /**
   * Delete a patient.
   *
   * @param id - Patient ID.
   * @returns Promise resolving to void.
   */
  deletePatient: (id: string) =>
    axiosInstance.delete<PatientResponseDto>(`/patients/${id}`),

  /**
   * Get patient by ID.
   *
   * @param id - Patient ID.
   * @returns Promise resolving to PatientResponseDto.
   */
  getPatientById: (id: string) =>
    axiosInstance.get<PatientResponseDto>(`/patients/${id}?include=address`),

  /**
   * Get patient subscriptions.
   *
   * @param id - Patient ID.
   * @returns Promise resolving to CommonEntitiesListResponse of PatientSubscriptionsProAppResponseDto.
   */
  getPatientsSubscriptions: (id: string) =>
    axiosInstance.get<
      CommonEntitiesListResponse<PatientSubscriptionsProAppResponseDto>
    >(`/patients/${id}/subscriptions`),

  /**
   * Get family members for a patient.
   *
   * @param patientId - Patient ID.
   * @returns Promise resolving to array of FamilyMemberResponseDto.
   */
  getFamilyMembers: (patientId: string) =>
    axiosInstance.get<FamilyMemberResponseDto[]>(
      `/pro/family-members/${patientId}`,
    ),

  /**
   * Create a new family member for a patient.
   *
   * @param patientId - Patient ID.
   * @param data - Family member creation DTO.
   * @returns Promise resolving to PatientResponseDto.
   */
  createFamilyMember: (patientId: string, data: FamilyMemberCreationDto) =>
    axiosInstance.post<FamilyMemberResponseDto>(
      `/pro/family-members/${patientId}`,
      data,
    ),

  /**
   * Delete a family member for a patient.
   *
   * @param patientId - Patient ID.
   * @param fmId - Family member ID.
   * @returns Promise resolving to void.
   */
  deleteFamilyMember: (patientId: string, fmId: string) =>
    axiosInstance.delete(`/pro/family-members/${fmId}/${patientId}`),

  /**
   * Update an existing family member for a patient.
   *
   * @param patientId - Patient ID.
   * @param memberId - Family member ID.
   * @param data - Family member update DTO.
   * @returns Promise resolving to FamilyMemberResponseDto.
   */
  updateFamilyMember: (
    patientId: string,
    memberId: string,
    data: FamilyMemberUpdateDto,
  ) =>
    axiosInstance.patch<FamilyMemberResponseDto>(
      `/pro/family-members/${memberId}/${patientId}`,
      data,
    ),

  /**
   * Download CSV template.
   *
   * @returns Promise resolving to Blob.
   */
  downloadCSVTemplate: () => {
    const locale = getLanguageFromLocalStorage();
    return axiosBlobInstance.get('/patients-management/templates/export', {
      params: { locale },
    });
  },

  /**
   * Upload CSV file.
   *
   * @param data - FormData containing the CSV file to upload.
   * @returns Promise resolving to void.
   */
  uploadCSV: (data: FormData) =>
    axiosInstance.post('/patients-management/import', data),
};
